import { EventEmitter } from 'events';
import store from '../store_instance';
import { zoomUI } from 'lib/zoom-ui';

class PresentationUI extends EventEmitter {

  constructor() {
    super();

    this.isPresenting = false;
    this.slides = [];
    this.index = null;
    this.currentWaypoint = null;
    this.pausedIndex = null;
    this.duration = 800;
    this.collapsedNodes = [];
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  start({ slides, startingIndex }) {
    const { canvas } = this;
    const { presentationControlsTag } = canvas;

    this.slides = slides;
    this.isPresenting = true;
    // capture current panzoom coords
    canvas.setPanzoomOptions({ duration: this.duration });
    canvas.closeSidebar();

    this.present({ index: startingIndex || 0 });
    this.emit('presentation-start');

    presentationControlsTag.setup();
    presentationControlsTag.show();
  }

  highlightNodes(nodeIds, fadeOutHighlight) {
    this.canvas.waypointIndicationBoxTag.show({ nodeIds });

    if (fadeOutHighlight) {
      setTimeout(this.unhighlightNodes, 1000);
    }
  }

  unhighlightNodes() {
    this.canvas.waypointIndicationBoxTag.hide();
  }

  panTo(slide, opts) {
    this.canvas.cancelTransition();
    const nodes = Array.isArray(slide) ? slide : [slide];
    this.canvas.centerContent({
      nodes: nodes.filter(n => Boolean(n))
    }, {
      animate: true, maxScale: zoomUI.maxScale, ignoreSidebars: true
    });

    if (opts && opts.doNotHighlight) return;

    $(canvas.refs.canvas).on('transitionend', (e) => {
      if (e.originalEvent.propertyName === 'transform') {
        $(canvas.refs.canvas).off('transitionend');
      }
    });
  }

  fadeOutSlide(nodeIds) {
    const { getNodeTag } = this.canvas;
    nodeIds.forEach(id => getNodeTag({ id }).fadeOut());
  }

  collapseExpandedAncestors() {
    const { getNodeTag } = this.canvas;
    this.collapsedNodes.forEach(id => getNodeTag({ id }).collapse());
    this.collapsedNodes = [];
  }

  expandSlideAncestors(nodeIds) {
    const { getNodeTag } = this.canvas;

    nodeIds.forEach(nodeId => {
      const node = store.getNode(nodeId);
      if (node) {
        node.ancestors().forEach(({ id }) => {
          const ancestor = store.getNode(id);

          if (ancestor.getIsCollapsed()) {
            getNodeTag({ id }).expand();
            this.collapsedNodes.push(id);
          }
        });
      }
    });
  }

  fadeInSlide(nodeIds) {
    document.body.classList.add('viewing-waypoint');

    for (const el of document.querySelectorAll('.waypoint-focused')) {
      el.classList.remove('waypoint-focused');
    }
    let allAffectedNodeIds = [];
    allAffectedNodeIds = allAffectedNodeIds.concat(nodeIds);
    for (const nodeId of nodeIds) {
      const node = this.canvas.store.getNode(nodeId);
      if (node) {
        allAffectedNodeIds = allAffectedNodeIds.concat(node.descendants().map(n => n.id));
      }
    }
    // links are in if both endpoints are in
    for (const nodeId of nodeIds) {
      const node = this.canvas.store.getNode(nodeId);
      if (!node) {
        continue;
      }
      this.canvas.getNodeTag({ id: nodeId }).fadeIn();

      // if a link node is in, then its R line is in too
      if (node.link()) {
        const link = node.link();
        const linkTag = this.canvas.linkTags[link.id];
        if (linkTag) {
          linkTag.fadeIn();
        }
      }

      // todo get this.descendants() for all nodes in waypoint and add to nodeIds
      for (const link of node.links()) {
        if (link.fromNodeId == node.id && allAffectedNodeIds.includes(link.toNodeId) || link.toNodeId == node.id && allAffectedNodeIds.includes(link.fromNodeId)) {
          const linkTag = this.canvas.linkTags[link.id];
          if (linkTag) {
            linkTag.fadeIn();
          }
          if (link.linkNode()) {
            this.canvas.getNodeTag({ id: link.linkNodeId }).fadeIn();
            this._fadeInSystem(link.linkNode());
          }
        }
      }

      this._fadeInSystem(node);
    }
  }

  // fade in all links within the given system
  _fadeInSystem(node) {
    const links = node.getSystemRelationships();
    if (links && links.length) {
      for (const link of links) {
        const linkTag = this.canvas.linkTags[link.id];
        if (linkTag) {
          linkTag.fadeIn();
        }
        if (link.linkNode()) {
          const nodeTag = this.canvas.getNodeTag({ id: link.linkNodeId });
          if (nodeTag) {
            nodeTag.fadeIn();
          }
        }
      }
    }
  }

  isNodeFocused(nodeId) {
    if (!this.currentWaypoint || !this.currentWaypoint.nodeIds) {
      return false;
    }
    if (this.currentWaypoint.nodeIds.find(id => id == nodeId)) {
      return true;
    }
    return false;
  }

  present({ index }) {
    this.pausedIndex = null;
    this.isPresenting = true;

    const {
      canvas,
      slides,
      duration,
      index: currentIndex,
    } = this;

    if (index == slides.length) {
      canvas.enablePanZoom();
      this.index = index;
      this.pausedIndex = null;
      this.stop();
      canvas.fitMapContents();
      return;
    } else if (index == slides.length + 1) {
      index = 0;
    } else if (index == -1) {
      index = slides.length - 1;
    }

    const upcomingSlide = slides[index] || slides[0];

    const upcomingNodeIds = upcomingSlide.nodeIds;
    const upcomingNodes = upcomingNodeIds.map(nodeId => store.getNode(nodeId));

    if (currentIndex !== null) {
      const prevSlide = slides[currentIndex];
      // Fade out previous slide
      if (prevSlide) {
        this.fadeOutSlide(prevSlide.nodeIds);
      }
    }

    this.collapseExpandedAncestors();
    // Expand any collapsed ancestors.
    this.expandSlideAncestors(upcomingNodeIds);
    // Fade in upcoming slide
    this.fadeInSlide(upcomingNodeIds);

    // Pan to it immediately
    canvas.enablePanZoom();
    this.canvas.presentationControlsTag.update();
    this.emit('presentation-present', { waypointId: upcomingSlide.id });
    this.panTo(upcomingNodes);

    this.index = index;
    this.currentWaypoint = upcomingSlide;
  }

  refresh() {
    this.present({ index: this.index });
  }

  prev() {
    this.present({ index: this.index - 1 });
  }

  next() {
    this.present({ index: this.index + 1 });
  }

  isPaused() {
    return this.pausedIndex !== null;
  }

  pause() {
    const pausedIndex = this.index;
    const waypointId = this.currentWaypoint ? this.currentWaypoint.id : null;
    this.stop();
    this.pausedIndex = pausedIndex;
    this.emit('presentation-pause', { waypointId });
  }

  resume() {
    const { canvas } = this;
    const { presentationControlsTag } = canvas;

    if (this.isPaused()) {
      this.present({ index: this.pausedIndex });
      this.emit('presentation-resume');

      presentationControlsTag.setup();
      presentationControlsTag.show();

      canvas.closeSidebar();
    }
  }

  stop(opts) {
    const { canvas, slides, duration } = this;

    this.isPresenting = false;
    this.index = null;
    this.currentWaypoint = null;
    this.pausedIndex = null;
    // Once the pan out animation ends, reset canvas panzoom options
    setTimeout(canvas.resetPanzoomOptions, duration);

    this.collapseExpandedAncestors();

    this.emit('presentation-stop');

    canvas.presentationControlsTag.hide();
    canvas.presentationControlsTag.shutdown();
    canvas.enablePanZoom();

    document.body.classList.remove('viewing-waypoint');
  }
}

// singleton instance
export default (new PresentationUI());



// WEBPACK FOOTER //
// ./starling/src/lib/presentation-ui.js